import { Any } from 'json2typescript';
import { Injectable } from '@angular/core';
import { ApiBaseService } from './api-base.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ErrorService } from '../error/error.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { CaseSearchResults, CaseSearchResultsLite, ClaimSearchResults, ClaimTypeSearchResults } from 'src/app/shared/models/cases/caseSearchResults';
import { CaseSearchParams } from 'src/app/shared/models/cases/caseSearchParams';
import { Case, CaseWrite } from 'src/app/shared/models/cases/case';
import { SearchParams } from 'src/app/shared/models/searchParams';
import { CaseSubjectSearchResults } from 'src/app/shared/models/cases/CaseSubjectSearchResults';
import { ClientOppDescription } from 'src/app/shared/models/cases/clientOppDescription';
import { DescriptionSearchResults } from 'src/app/shared/models/cases/descriptionSearchResults';
import { CaseSubject } from 'src/app/shared/models/cases/caseSubject';
import { DocumentSearchResults } from 'src/app/shared/models/documents/documentSearchResults';
import { CaseLawyer, CaseLawyerHistory } from 'src/app/shared/models/cases/caseLawyer';
import { CaseClient } from 'src/app/shared/models/cases/caseClient';
import { CaseClientContact } from 'src/app/shared/models/cases/caseClientContact';
import { CaseOpponent } from 'src/app/shared/models/cases/caseOpponent';
import { Document } from 'src/app/shared/models/documents/document';
import { ClaimType } from 'src/app/shared/models/cases/claimType';
import { Claim } from 'src/app/shared/models/cases/claim';
import { FileTimesheet } from 'src/app/shared/models/timesheet/timesheetSearchResults';


@Injectable({
    providedIn: 'root'
})
export class FilesApiService extends ApiBaseService {

    baseUrl = environment.backend_host + '/api/cases';

    filesResult: CaseSearchResultsLite;

    constructor(
        public http: HttpClient,
        protected errorService: ErrorService,
        protected spinner: NgxSpinnerService) {
        super(spinner, errorService);
    }

    /***********************
    // FILES OR CASES
    /***********************/

    // user this with files report
    public getFiles = (params: CaseSearchParams): Observable<CaseSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/`, { params: p })
            .pipe(map(this.deserializeAs(CaseSearchResults)));
    }

    // use this with listing files in tables
    public getFilesLite = (params: CaseSearchParams): Observable<CaseSearchResultsLite> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/list-lite`, { params: p })
            .pipe(map(this.deserializeAs(CaseSearchResultsLite)));
    }

    // Use this with ng-selects
    public getFilesTiny = (params: CaseSearchParams): Observable<CaseSearchResultsLite> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/case-lite`, { params: p })
            .pipe(map(this.deserializeAs(CaseSearchResultsLite)));
    }

    public getFileSummary = (id: number): Observable<Case> => {
        return this.http.get<any>(`${this.baseUrl}/${id}`)
            .pipe(map(this.deserializeAs(Case)));
    }

    public createCase = (c: CaseWrite): Observable<CaseWrite> => {
        return this.http.post<any>(`${this.baseUrl}/`, this.serialize(c))
            .pipe(map(this.deserializeAs(CaseWrite)));
    }

    public updateCase = (c: CaseWrite): Observable<CaseWrite> => {
        return this.http.put<any>(`${this.baseUrl}/${c.id}/update`, this.serialize(c))
            .pipe(map(this.deserializeAs(CaseWrite)));
    }

    public deleteCase = (c: Case): Observable<any> => {
        return this.http.delete<any>(`${this.baseUrl}/${c.id}`)
            .pipe(share());
    }

    public getFileDocuments = (id: number, params: SearchParams): Observable<DocumentSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/${id}/documents`, { params: p })
            .pipe(map(this.deserializeAs(DocumentSearchResults)));
    }

    public uploadFileDocument = (id: number, formData: FormData): Observable<DocumentSearchResults> =>
        this.http.post(`${this.baseUrl}/${id}/documents`, formData)
            .pipe(map(this.deserializeAs(DocumentSearchResults)));


    public acceptNewCase = (id: number): Observable<any> => {
        return this.http.put<any>(`${this.baseUrl}/${id}/accept`, {});
    }

    public rejectNewCase = (id: number, reason: string): Observable<any> => {
        return this.http.put<any>(`${this.baseUrl}/${id}/reject`, { reason });
    }

    public confirmCaseClose = (id: number): Observable<any> => {
        return this.http.put<any>(`${this.baseUrl}/${id}/confirm-close`, {});
    }

    public rejectCaseClose = (id: number): Observable<any> => {
        return this.http.put<any>(`${this.baseUrl}/${id}/reject-close`, {});
    }

    public confirmCaseReopen = (id: number): Observable<any> => {
        return this.http.put<any>(`${this.baseUrl}/${id}/reopen`, {});
    }

    public rejectCaseReopen = (id: number): Observable<any> => {
        return this.http.put<any>(`${this.baseUrl}/${id}/reject-reopen`, {});
    }

    public closingRemarks = (id: number, remarks: string): Observable<any> => {
        return this.http.put<any>(`${this.baseUrl}/${id}/closing-remarks`, { remarks });
    }

    public reopenCaseRequest = (id: number): Observable<any> => {
        return this.http.put<any>(`${this.baseUrl}/${id}/request-reopen`, {});
    }

    public getFileTimesheets = (id: number, params: SearchParams): Observable<FileTimesheet> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/${id}/timesheet`, { params: p })
            .pipe(map(this.deserializeAs(FileTimesheet)));
    }

    /***********************
       // Case Details
       /***********************/

    public createCaseLawyer = (l: CaseLawyer): Observable<CaseLawyer> => {
        return this.http.post<any>(`${this.baseUrl}/${l.caseId}/case-lawyers`, this.serialize(l))
            .pipe(map(this.deserializeAs(CaseLawyer)));
    }
    public updateCaseLawyer = (l: CaseLawyer): Observable<CaseLawyer> => {
        return this.http.put<any>(`${this.baseUrl}/${l.caseId}/case-lawyers/${l.id}`, this.serialize(l))
            .pipe(map(this.deserializeAs(CaseLawyer)));
    }
    public deleteCaseLawyer = (l: CaseLawyer): Observable<any> => {
        return this.http.delete<any>(`${this.baseUrl}/${l.caseId}/case-lawyers/${l.id}`)
            .pipe(share());
    }
    public caseLawyerHistory = (id: number): Observable<CaseLawyerHistory[]> => {
        return this.http.get<any>(`${this.baseUrl}/${id}/case-lawyers/history`)
            .pipe(map(this.deserializeArrayAs(CaseLawyerHistory)));
    }

    public createCaseClient = (c: CaseClient): Observable<CaseClient> => {
        return this.http.post<any>(`${this.baseUrl}/${c.caseId}/clients`, this.serialize(c))
            .pipe(map(this.deserializeAs(CaseClient)));
    }
    public updateCaseClient = (c: CaseClient): Observable<CaseClient> => {
        return this.http.put<any>(`${this.baseUrl}/${c.caseId}/clients/${c.id}`, this.serialize(c))
            .pipe(map(this.deserializeAs(CaseClient)));
    }
    public deleteCaseClient = (c: CaseClient): Observable<any> => {
        return this.http.delete<any>(`${this.baseUrl}/${c.caseId}/clients/${c.id}`)
            .pipe(share());
    }

    public createCaseClientContact = (c: CaseClientContact): Observable<CaseClientContact> => {
        return this.http.post<any>(`${this.baseUrl}/${c.caseId}/client-contacts`, this.serialize(c))
            .pipe(map(this.deserializeAs(CaseClientContact)));
    }
    public updateCaseClientContact = (c: CaseClientContact): Observable<CaseClientContact> => {
        return this.http.put<any>(`${this.baseUrl}/${c.caseId}/client-contacts/${c.id}`, this.serialize(c))
            .pipe(map(this.deserializeAs(CaseClientContact)));
    }
    public deleteCaseClientContact = (c: CaseClientContact): Observable<any> => {
        return this.http.delete<any>(`${this.baseUrl}/${c.caseId}/client-contacts/${c.id}`)
            .pipe(share());
    }

    public createCaseOpponents = (o: CaseOpponent): Observable<CaseOpponent> => {
        return this.http.post<any>(`${this.baseUrl}/${o.caseId}/opponents`, this.serialize(o))
            .pipe(map(this.deserializeAs(CaseOpponent)));
    }
    public updateCaseOpponents = (o: CaseOpponent): Observable<CaseOpponent> => {
        return this.http.put<any>(`${this.baseUrl}/${o.caseId}/opponents/${o.id}`, this.serialize(o))
            .pipe(map(this.deserializeAs(CaseOpponent)));
    }
    public deleteCaseOpponents = (o: CaseOpponent): Observable<any> => {
        return this.http.delete<any>(`${this.baseUrl}/${o.caseId}/opponents/${o.id}`)
            .pipe(share());
    }


    /***********************
    // Case Subjects
    /***********************/

    public getCaseSubjects = (params: SearchParams): Observable<CaseSubjectSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/case-subjects`, { params: p })
            .pipe(map(this.deserializeAs(CaseSubjectSearchResults)));
    }

    public getCaseSubject = (id: number): Observable<CaseSubject> => {
        return this.http.get<any>(`${this.baseUrl}/case-subjects/${id}`)
            .pipe(map(this.deserializeAs(CaseSubject)));
    }

    public createCaseSubject = (c: CaseSubject): Observable<CaseSubject> => {
        return this.http.post<any>(`${this.baseUrl}/case-subjects`, this.serialize(c))
            .pipe(map(this.deserializeAs(CaseSubject)));
    }

    public updateCaseSubject = (c: CaseSubject): Observable<CaseSubject> => {
        return this.http.put<any>(`${this.baseUrl}/case-subjects/${c.id}`, this.serialize(c))
            .pipe(map(this.deserializeAs(CaseSubject)));
    }

    public deleteCaseSubject = (c: CaseSubject): Observable<any> => {
        return this.http.delete<any>(`${this.baseUrl}/case-subjects/${c.id}`)
            .pipe(share());
    }

    /***********************
    // Case Descriptions
    /***********************/

    public getDescriptions = (params: SearchParams): Observable<DescriptionSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/descriptions`, { params: p })
            .pipe(map(this.deserializeAs(DescriptionSearchResults)));
    }

    public getDescription = (id: number): Observable<ClientOppDescription> => {
        return this.http.get<any>(`${this.baseUrl}/descriptions/${id}`)
            .pipe(map(this.deserializeAs(ClientOppDescription)));
    }

    public createDescription = (c: ClientOppDescription): Observable<ClientOppDescription> => {
        return this.http.post<any>(`${this.baseUrl}/descriptions`, this.serialize(c))
            .pipe(map(this.deserializeAs(ClientOppDescription)));
    }

    public updateDescription = (c: ClientOppDescription): Observable<ClientOppDescription> => {
        return this.http.put<any>(`${this.baseUrl}/descriptions/${c.id}`, this.serialize(c))
            .pipe(map(this.deserializeAs(ClientOppDescription)));
    }

    public deleteDescription = (c: ClientOppDescription): Observable<any> => {
        return this.http.delete<any>(`${this.baseUrl}/descriptions/${c.id}`)
            .pipe(share());
    }

    // Claim Types
    public getClaimTypes = (params: SearchParams): Observable<ClaimTypeSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/claim-types`, { params: p })
            .pipe(map(this.deserializeAs(ClaimTypeSearchResults)));
    }

    public getClaimType = (id: number): Observable<ClaimType> => {
        return this.http.get<any>(`${this.baseUrl}/claim-types/${id}`)
            .pipe(map(this.deserializeAs(ClaimType)));
    }

    public createClaimType = (c: ClaimType): Observable<ClaimType> => {
        return this.http.post<any>(`${this.baseUrl}/claim-types`, this.serialize(c))
            .pipe(map(this.deserializeAs(ClaimType)));
    }

    public updateClaimType = (c: ClaimType): Observable<ClaimType> => {
        return this.http.put<any>(`${this.baseUrl}/claim-types/${c.id}`, this.serialize(c))
            .pipe(map(this.deserializeAs(ClaimType)));
    }

    public deleteClaimType = (c: ClaimType): Observable<ClaimType> => {
        return this.http.delete<any>(`${this.baseUrl}/claim-types/${c.id}`)
            .pipe(share());
    }



    // Claim
    public getClaims = (caseId: number): Observable<ClaimSearchResults> => {
        return this.http.get<any>(`${this.baseUrl}/${caseId}/claims`)
            .pipe(map(this.deserializeAs(ClaimSearchResults)));
    }

    public getClaim = (claimId: number, caseId: number): Observable<Claim> => {
        return this.http.get<any>(`${this.baseUrl}/${caseId}/claims/${claimId}`)
            .pipe(map(this.deserializeAs(Claim)));
    }

    public createClaim = (claim: Claim): Observable<Claim> => {
        return this.http.post<any>(`${this.baseUrl}/${claim.caseId}/claims`, this.serialize(claim))
            .pipe(map(this.deserializeAs(Claim)));
    }

    public updateClaim = (claim: Claim): Observable<Claim> => {
        return this.http.put<any>(`${this.baseUrl}/${claim.caseId}/claims/${claim.id}`, this.serialize(claim))
            .pipe(map(this.deserializeAs(Claim)));
    }

    public deleteClaim = (claim: Claim): Observable<Claim> => {
        return this.http.delete<any>(`${this.baseUrl}/${claim.caseId}/claims/${claim.id}`)
            .pipe(share());
    }

}